<template>
  <core-view>

    <div class="seletor-armazem">
      <card-auto-complete-local
        v-model="idlocal"
        :dados="armazem"
        label="Local Armazenamento"
        icon="mdi-qrcode-scan"
        item-value="lmeidlocalmovimentacaoestoque"
        :action="$_onClickQR"
        :readonly="!armazem || !armazem.length"
        @localSelecionado="$_carregarDados"
      />
    </div>

    <table-grid
      :periodo="periodo"
      :colunas="colunas"
      :dados="dados"
      titulo="Controle Armazenamento"
      tituloVazio="Selecione um local de armazenamento"
      :loading="loading"
      :resumo="resumo"
      :agrupamentos="agrupamentos"
      @clicou="$_onClickItem"
    />

  </core-view>
</template>

<script>
import { mapActions, mapMutations, mapState } from 'vuex';
import moment from 'moment';

import { CONTROLE_ARMAZENAMENTO_QUERIES } from '@/constants/QUERY_FILTERS';
import { ROUTE_QR } from '@/constants/ROUTES';
import { getQueryValue } from '@/services/query-string';
import { CONTROLE_ARMAZENAMENTO_STORAGE } from '@/constants/STORAGE';

import CoreView from '@/components/core/view/Index.vue';
import TableGrid from '@/components/core/table-grid/Index.vue';
import CardAutoCompleteLocal from '@/components/core/card-auto-complete-local/Index.vue';
import errorHandler from '@/utils/error-handler';
import deepEquals from '@/utils/deep-equals';

function getSessionStorage(key, defaultValue) {
  return sessionStorage.getItem(key) ?? defaultValue;
}

const today = new Date().toISOString().substring(0, 10);

export default {
  name: 'ControleArmazenamentoIndex',

  components: {
    CoreView,
    TableGrid,
    CardAutoCompleteLocal,
  },

  data() {
    return {
      loading: false,
      dados: [],
      idlocal: getSessionStorage(CONTROLE_ARMAZENAMENTO_STORAGE.idlocal, null)
        ? Number(getSessionStorage(CONTROLE_ARMAZENAMENTO_STORAGE.idlocal, null))
        : null,

      filters: {
        createdFrom: getSessionStorage(CONTROLE_ARMAZENAMENTO_STORAGE.createFrom, today),
        createdBefore: getSessionStorage(CONTROLE_ARMAZENAMENTO_STORAGE.createBefore, today),
      },

      colunas: [
        {
          key: 'resdatarecebimento',
          nome: 'Data',
          mascara: 'DD/MM/YYYY',
          headerClasse: 'px-4 colunas',
          linhaClasse: 'px-4 td-texto destaque',
        },
        {
          key: 'ablnrocontrole',
          nome: 'Lote',
          headerClasse: 'px-3 colunas',
          linhaClasse: 'px-5 destaque',
        },

        {
          key: 'resprodutor',
          nome: 'Produtor',
          headerClasse: 'px-3 colunas',
          linhaClasse: 'td-texto destaque',
        },
        {
          key: 'respropriedade',
          nome: 'Propriedade',
          headerClasse: 'colunas',
          linhaClasse: 'td-texto destaque',
        },
        {
          key: 'connumero',
          nome: 'Contrato',
          headerClasse: 'px-3 colunas',
          linhaClasse: 'td-texto destaque',
        },
        {
          key: 'resproduto',
          nome: 'Produto',
          headerClasse: 'px-3 colunas',
          linhaClasse: 'td-texto destaque',
        },
        {
          key: 'resvariedade',
          nome: 'Variedade',
          headerClasse: 'px-3 colunas',
          linhaClasse: 'destaque',
        },
        {
          key: 'resplaca',
          nome: 'Placa',
          headerClasse: 'px-3 colunas',
          linhaClasse: 'px-3 destaque',
        },
        {
          key: 'respesoliquido',
          nome: 'Peso Líq.',
          decimal: true,
          casas: 0,
          headerClasse: 'px-4 colunas',
          linhaClasse: 'pr-1 td-numero destaque',
        },
        {
          key: 'resumidade',
          nome: 'Umidade',
          decimal: true,
          casas: 2,
          headerClasse: 'px-3 colunas',
          linhaClasse: 'destaque',
        },
        {
          key: 'resafla',
          nome: 'Afla',
          decimal: true,
          casas: 2,
          headerClasse: 'px-3 colunas',
          linhaClasse: 'destaque',
        },
        {
          key: 'resimpureza',
          nome: 'Impureza',
          decimal: true,
          casas: 2,
          headerClasse: 'px-3 colunas',
          linhaClasse: 'destaque',
        },
      ],

      resumo: {
        colunas: [
          {
            key: 'viagens',
            label: 'Qtd.',
            tipo: 'count',
            headerClasse: 'px-3',
            linhaClasse: 'td-numero destaque',
          },
          {
            key: 'respesoliquido',
            label: 'Peso Líq.',
            tipo: 'sum',
            headerClasse: 'px-3',
            linhaClasse: 'td-numero destaque',
          },
          {
            key: 'resumidade',
            label: 'Umidade Méd.',
            tipo: 'media',
            headerClasse: 'px-3',
            linhaClasse: 'td-numero destaque',
          },
          {
            key: 'resafla',
            label: 'Afla Méd.',
            tipo: 'media',
            headerClasse: 'px-3',
            linhaClasse: 'td-numero destaque',
          },
          {
            key: 'resimpureza',
            label: 'Impureza Méd.',
            tipo: 'media',
            headerClasse: 'px-3',
            linhaClasse: 'td-numero destaque',
          },
        ],
      },

      agrupamentos: [
        {
          label: 'Produto / Variedade',
          groupBy: ['resproduto', 'resvariedade'],
          coluna: [
            {
              key: 'resproduto',
              label: 'Produto',
              headerClasse: 'px-3',
              linhaClasse: 'td-texto destaque',
            },
            {
              key: 'resvariedade',
              label: 'Variedade',
              headerClasse: 'px-3',
              linhaClasse: 'td-texto destaque',
            },
          ],
        },
        {
          label: 'Produtor / Propriedade',
          groupBy: ['resprodutor', 'respropriedade'],
          coluna: [
            {
              key: 'resprodutor',
              label: 'Produtor',
              headerClasse: 'px-3',
              linhaClasse: 'td-texto destaque',
            },
            {
              key: 'respropriedade',
              label: 'Propriedade',
              headerClasse: 'px-3',
              linhaClasse: 'td-texto destaque',
            },
          ],
        },
      ],
    };
  },

  computed: {
    ...mapState({
      armazem: (state) => state.controlearmazenamento.armazem,
    }),

    periodo() {
      const from = this.filters.createdFrom
        ? moment(this.filters.createdFrom).format('DD/MM/YYYY')
        : '---';
      const before = this.filters.createdBefore
        ? moment(this.filters.createdBefore).format('DD/MM/YYYY')
        : '---';
      return `${from} até ${before}`;
    },
  },

  methods: {
    ...mapActions({
      $_findControleArmazenamento: 'controlearmazenamento/findControleArmazenamento',
      $_findArmazem: 'controlearmazenamento/findArmazem',
    }),
    ...mapMutations({
      $_setLocal: 'controlearmazenamento/SET_LOCAL',
    }),

    async $_carregarArmazens() {
      try {
        await this.$_findArmazem();
      } catch (err) {
        errorHandler.call(this, err);
      }
    },

    async $_carregarDados() {
      if (!this.idlocal) return;

      this.loading = true;
      try {
        const where = {
          residlocalmovimentacaoestoque: this.idlocal,
        };

        if (this.filters.createdFrom) {
          where['resdatarecebimento:gte'] = new Date(this.filters.createdFrom).toJSON();
        }
        if (this.filters.createdBefore) {
          where['resdatarecebimento:lte'] = new Date(`${this.filters.createdBefore}T23:59:00`).toJSON();
        }

        const { data } = await this.$_findControleArmazenamento({
          orderBy: 'resdatarecebimento',
          limit: 9999,
          offset: 0,
          where,
        });

        this.dados = data;
      } catch (err) {
        errorHandler.call(this, err);
      } finally {
        this.loading = false;
      }
    },

    $_onClickQR() {
      sessionStorage.setItem('rota-anterior', this.$route.path);
      this.$router.push({
        ...this.$route,
        name: ROUTE_QR.name,
        params: { tipo: 'armazem' },
      });
    },

    $_verificaQR() {
      const qr = sessionStorage.getItem('qrcodeleitura');
      if (!qr) return;

      sessionStorage.removeItem('qrcodeleitura');

      const selecionado = (this.armazem || []).find(
        (a) => String(a.lmecodigoarmazem) === String(qr),
      );

      if (!selecionado) {
        errorHandler.call(this, new Error(`Armazém "${qr}" não encontrado`));
        return;
      }

      this.idlocal = selecionado.lmeidlocalmovimentacaoestoque;
      this.$_setLocal(selecionado.lmeidlocalmovimentacaoestoque);
      this.$_carregarDados();
    },

    $_onClickItem() {
      // Extensão futura: abrir detalhe do recebimento
    },

    $_syncFiltersFromRoute() {
      const newFilters = {
        createdFrom: getQueryValue(
          CONTROLE_ARMAZENAMENTO_QUERIES.createdFrom,
          this.filters.createdFrom,
        ),
        createdBefore: getQueryValue(
          CONTROLE_ARMAZENAMENTO_QUERIES.createdBefore,
          this.filters.createdBefore,
        ),
      };

      if (!deepEquals(this.filters, newFilters)) {
        this.filters = newFilters;

        sessionStorage.setItem(CONTROLE_ARMAZENAMENTO_STORAGE.createFrom, newFilters.createdFrom ?? '');
        sessionStorage.setItem(CONTROLE_ARMAZENAMENTO_STORAGE.createBefore, newFilters.createdBefore ?? '');

        if (this.idlocal) this.$_carregarDados();
      }
    },
  },

  watch: {
    $route: {
      deep: true,
      immediate: false,
      handler() {
        this.$_syncFiltersFromRoute();
        this.$_verificaQR();
      },
    },

    idlocal(val) {
      this.$_setLocal(val);
      sessionStorage.setItem(CONTROLE_ARMAZENAMENTO_STORAGE.idlocal, val ?? '');
    },
  },

  async created() {
    await this.$_carregarArmazens();
    this.$_verificaQR();
    // Restaura o local selecionado no Vuex após reload/troca de view
    if (this.idlocal) {
      this.$_setLocal(this.idlocal);
      this.$_carregarDados();
    }
  },

  destroyed() {
    sessionStorage.removeItem('qrcodeleitura');
  },
};
</script>

<style scoped>
.seletor-armazem {
  max-width: 400px;
  margin: 12px auto 0;
  padding: 0 16px;
}
</style>
